<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Switch Component
      p.text--secondary Toggle switches allow users to turn an option on or off.

    // Switch Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in switchVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            // Basic switch with value display
            template(v-if="variation.id === 'basic-switch'")
              .d-flex.flex-wrap.gap-4.align-center
                div(v-for="switchItem in variation.switches" :key="switchItem.value" class="d-flex flex-column align-center")
                  yz-switch(v-model="$data[switchItem.model]" v-bind="switchItem.props")
                  small.text--secondary.mt-2 {{ switchItem.label }}
                  small.text--secondary.mt-1 Value: {{ JSON.stringify($data[switchItem.model]) }}
            // Size variations
            template(v-else-if="variation.id === 'switch-sizes'")
              .d-flex.flex-wrap.gap-4.align-center
                div(v-for="switchItem in variation.switches" :key="switchItem.size" class="d-flex flex-column align-center" style="min-width: 100px;")
                  yz-switch(v-model="$data[switchItem.model]" v-bind="switchItem.props")
                  small.text--secondary.mt-2 {{ switchItem.label }}
                  small.text--secondary.mt-1 Value: {{ JSON.stringify($data[switchItem.model]) }}
            // Disabled states with spacing
            template(v-else-if="variation.id === 'disabled-states'")
              .d-flex.flex-wrap.gap-4.align-center
                div(v-for="switchItem in variation.switches" :key="switchItem.value" class="d-flex flex-column align-center")
                  yz-switch(v-model="$data[switchItem.model]" v-bind="switchItem.props")
                  small.text--secondary.mt-2 {{ switchItem.hint }}
                  small.text--secondary.mt-1 Value: {{ JSON.stringify($data[switchItem.model]) }}
            // V-form integration examples
            template(v-else-if="variation.id === 'vform-integration'")
              .mb-4
                h5.mb-2 V-form with Multiple Switches
                v-form(ref="demoForm")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Terms and Conditions
                    yz-switch(v-model="vformTermsValue" required required-error-message="You must accept the terms and conditions")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Email Notifications
                    yz-switch(v-model="vformNotificationsValue" :rules="[v => v === true || 'Email notifications are required for this demo']")
                  .mb-3
                    label.yz-regular-body-sm.font-weight-medium.mb-2 Marketing Communications
                    yz-switch(v-model="vformMarketingValue")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetVForm")
                      yz-btn(label="Validate" size="sm" outlined, @click="validateVForm")
                    .mt-2(v-if="vformValidationResult")
                      span.yz-regular-body-xs(:class="{ 'success--text': vformValidationResult === 'Valid', 'error--text': vformValidationResult === 'Invalid' }") {{ vformValidationResult }}
                  .mt-3
                    small.text--secondary Form Values: {{ JSON.stringify({ terms: vformTermsValue, notifications: vformNotificationsValue, marketing: vformMarketingValue }) }}

            // Rules-based validation examples  
            template(v-else-if="variation.id === 'rules-validation'")
              .mb-4
                h5.mb-2 Custom Validation Rules
                .mb-3
                  label.yz-regular-body-sm.font-weight-medium.mb-2 Enable Feature A
                  yz-switch(v-model="rulesFeatureA" :rules="customRulesA")
                .mb-3
                  label.yz-regular-body-sm.font-weight-medium.mb-2 Enable Feature B  
                  yz-switch(v-model="rulesFeatureB" :rules="customRulesB")
                .mb-3
                  label.yz-regular-body-sm.font-weight-medium.mb-2 At least one feature must be enabled
                  .text--secondary.caption Combined validation: {{ atLeastOneFeatureEnabled ? 'Valid' : 'At least one feature must be enabled' }}
                .mt-3
                  small.text--secondary Values: {{ JSON.stringify({ featureA: rulesFeatureA, featureB: rulesFeatureB }) }}

            // Icon switches with spacing
            template(v-else-if="variation.id === 'switch-icons'")
              .d-flex.flex-wrap.gap-4.align-center
                div(v-for="switchItem in variation.switches" :key="switchItem.value" class="d-flex flex-column align-center")
                  yz-switch(v-model="$data[switchItem.model]" v-bind="switchItem.props")
                    template(#icon v-if="switchItem.hasIcon")
                      v-icon(v-if="switchItem.props.disabled" color="yz-neutral-400") {{ switchItem.iconName }}
                      v-icon(v-else :color="$data[switchItem.model] ? 'yz-success' : 'yz-neutral-400'") {{ switchItem.iconName }}
                  small.text--secondary.mt-2 {{ switchItem.label }}
                  small.text--secondary.mt-1 Value: {{ JSON.stringify($data[switchItem.model]) }}
            // Regular switches
            template(v-else)
              yz-switch(v-for="switchItem in variation.switches" :key="switchItem.value" v-model="$data[switchItem.model]" v-bind="switchItem.props")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              span.font-weight-medium Code Examples

            .code-examples
              // Special handling for v-form and rules sections
              template(v-if="variation.id === 'vform-integration'")
                .mb-3
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === vformExampleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === vformExampleCode ? 'success' : ''" @click="copyCode(vformExampleCode)")
                    span.font-weight-medium.ml-2 V-form Integration Example
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ vformExampleCode }}

              template(v-else-if="variation.id === 'rules-validation'")
                .mb-3
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === rulesExampleCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === rulesExampleCode ? 'success' : ''" @click="copyCode(rulesExampleCode)")
                    span.font-weight-medium.ml-2 Custom Rules Example
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ rulesExampleCode }}

              // Regular handling for other sections
              template(v-else)
                div(v-for="switchItem in variation.switches" :key="switchItem.value || switchItem.size" class="mb-3")
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === switchItem.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === switchItem.code ? 'success' : ''" @click="copyCode(switchItem.code)")
                    span.font-weight-medium.ml-2 {{ switchItem.label }}
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ switchItem.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      // YzSwitch Props
      h4.mb-3 YzSwitch Props
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in switchProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      // Methods Documentation
      h4.mb-3.mt-6 YzSwitch Methods
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Method
                th Description
                th Returns
            tbody
              tr(v-for="method in switchMethods" :key="method.name")
                td.font-weight-medium.text-no-wrap {{ method.name }}
                td.single-line {{ method.description }}
                td.text--secondary.text-no-wrap {{ method.returns }}

      // Slots Documentation
      h4.mb-3.mt-6 YzSwitch Slots
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot Name
                th Description
            tbody
              tr(v-for="slot in switchSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      basicSwitchValue: false,
      disabledOffSwitchValue: false,
      disabledOnSwitchValue: true,
      xsSwitchValue: false,
      smSwitchValue: true,
      mdSwitchValue: false,
      lgSwitchValue: true,
      iconSwitchValue: false,
      iconDisabledSwitchValue: false,
      // V-form example data
      vformTermsValue: false,
      vformNotificationsValue: false,
      vformMarketingValue: true,
      vformValidationResult: null,
      // Rules-based validation data
      rulesFeatureA: false,
      rulesFeatureB: false,
      switchVariations: [
        {
          name: 'Basic Switch',
          id: 'basic-switch',
          description: 'Standard toggle switch',
          switches: [
            {
              label: 'Basic Switch',
              value: 'basic',
              model: 'basicSwitchValue',
              props: {},
              code: 'yz-switch(v-model="switchValue")'
            }
          ]
        },
        {
          name: 'Switch Sizes',
          id: 'switch-sizes',
          description: 'Different switch sizes for various use cases',
          switches: [
            {
              label: 'Extra Small (xs)',
              size: 'xs',
              model: 'xsSwitchValue',
              props: {size: 'xs'},
              code: 'yz-switch(v-model="switchValue" size="xs")'
            },
            {
              label: 'Small (sm)',
              size: 'sm',
              model: 'smSwitchValue',
              props: {size: 'sm'},
              code: 'yz-switch(v-model="switchValue" size="sm")'
            },
            {
              label: 'Medium (md)',
              size: 'md',
              model: 'mdSwitchValue',
              props: {size: 'md'},
              code: 'yz-switch(v-model="switchValue" size="md")'
            },
            {
              label: 'Large (lg)',
              size: 'lg',
              model: 'lgSwitchValue',
              props: {size: 'lg'},
              code: 'yz-switch(v-model="switchValue" size="lg")'
            }
          ]
        },
        {
          name: 'Disabled States',
          id: 'disabled-states',
          description: 'Disabled switch states',
          switches: [
            {
              label: 'Disabled (Off)',
              value: 'disabled-off',
              model: 'disabledOffSwitchValue',
              props: {disabled: true},
              code: 'yz-switch(v-model="switchValue" disabled)',
              hint: 'Disabled off state'
            },
            {
              label: 'Disabled (On)',
              value: 'disabled-on',
              model: 'disabledOnSwitchValue',
              props: {disabled: true},
              code: 'yz-switch(v-model="switchValue" disabled)',
              hint: 'Disabled on state'
            }
          ]
        },
        {
          name: 'V-form Integration',
          id: 'vform-integration',
          description: 'Using YzSwitch with Vuetify v-form for comprehensive form validation',
          switches: []
        },
        {
          name: 'Rules-based Validation',
          id: 'rules-validation',
          description: 'Custom validation rules with the rules prop',
          switches: []
        },
        {
          name: 'Switch with Icons',
          id: 'switch-icons',
          description: 'Switches with custom icons in the slider',
          switches: [
            {
              label: 'Power Switch',
              value: 'icon-power',
              model: 'iconSwitchValue',
              props: {},
              code: 'yz-switch(v-model="switchValue")\n  template(#icon)\n    v-icon mdi-power',
              hasIcon: true,
              iconName: 'mdi-power'
            },
            {
              label: 'Heart Switch',
              value: 'icon-heart',
              model: 'iconDisabledSwitchValue',
              props: {disabled: true},
              code: 'yz-switch(v-model="switchValue" disabled)\n  template(#icon)\n    v-icon mdi-heart',
              hasIcon: true,
              iconName: 'mdi-heart'
            }
          ]
        }
      ],
      switchProps: [
        {
          name: 'v-model',
          type: 'Boolean',
          default: 'false',
          description: 'The switch state (true for on, false for off)'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the switch is disabled'
        },
        {
          name: 'required',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the switch is required. Shows error message when validation fails'
        },
        {
          name: 'requiredErrorMessage',
          type: 'String',
          default: "'This field is required'",
          description: 'Error message to display when required validation fails. Used when required=true.'
        },
        {
          name: 'size',
          type: 'String',
          default: 'lg',
          description: 'Switch size: xs, sm, md, lg'
        },
        {
          name: 'rules',
          type: 'Array',
          default: '[]',
          description: 'Array of validation functions. Each function should return true or an error message string'
        }
      ],
      switchMethods: [
        {
          name: 'validate()',
          description: 'Manually trigger validation and return validation result',
          returns: 'Boolean'
        },
        {
          name: 'focus()',
          description: 'Focus the switch input element',
          returns: 'void'
        },
        {
          name: 'blur()',
          description: 'Blur the switch input element',
          returns: 'void'
        },
        {
          name: 'reset()',
          description: 'Reset validation state and clear error messages. Optionally resets value',
          returns: 'void'
        },
        {
          name: 'resetValidation()',
          description: 'Clear validation errors without resetting the switch value',
          returns: 'void'
        }
      ],
      switchSlots: [
        {
          name: 'icon',
          description: 'Custom icon content displayed in the center of the switch slider'
        }
      ],
      // Code examples for new sections
      vformExampleCode: `// Template
v-form(ref="demoForm")
  .mb-3
    label Terms and Conditions
    yz-switch(v-model="termsValue" required required-error-message="You must accept the terms")
  .mb-3
    label Email Notifications
    yz-switch(v-model="notificationValue" :rules="[v => v === true || 'Notifications required']")
  .mb-3
    label Marketing Communications
    yz-switch(v-model="marketingValue")
  .mt-4
    yz-btn(label="Validate Form" @click="validateForm")
    yz-btn(label="Reset Form" @click="resetForm")

// Script
methods: {
  validateForm() {
    const isValid = this.$refs.demoForm.validate();
    console.log('Form is valid:', isValid);
  },
  resetForm() {
    this.$refs.demoForm.reset();
  }
}`,
      rulesExampleCode: `// Template
yz-switch(v-model="featureA" :rules="customRulesA")
yz-switch(v-model="featureB" :rules="customRulesB")

// Script
data() {
  return {
    featureA: false,
    featureB: false
  }
},
computed: {
  customRulesA() {
    return [
      v => !!v || 'Feature A must be enabled for this demo'
    ]
  },
  customRulesB() {
    return [
      v => !!v || 'Feature B must be enabled for this demo'  
    ]
  }
}`
    }
  },
  computed: {
    atLeastOneFeatureEnabled() {
      return this.rulesFeatureA || this.rulesFeatureB;
    },
    customRulesA() {
      return [
        v => !!v || 'Feature A must be enabled for this demo'
      ]
    },
    customRulesB() {
      return [
        v => !!v || 'Feature B must be enabled for this demo'
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      // Update the route with the section hash
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    validateVForm() {
      if (this.$refs.demoForm) {
        const demoFormArray = this.$refs.demoForm;
        const demoFormRef = Array.isArray(demoFormArray) ? demoFormArray[0] : demoFormArray;
        const isValid = demoFormRef.validate();
        this.vformValidationResult = isValid ? 'Valid' : 'Invalid';
        setTimeout(() => {
          this.vformValidationResult = null;
        }, 1500);
      }
    },
    resetVForm() {
      if (this.$refs.demoForm) {
        const demoFormArray = this.$refs.demoForm;
        const demoFormRef = Array.isArray(demoFormArray) ? demoFormArray[0] : demoFormArray;
        demoFormRef.resetValidation();
        this.vformValidationResult = null;
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-3 > *:not(:last-child) {
  margin-bottom: 0.75rem;
}
</style>